import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  getFleetIncidentEndpoint,
  getFleetIncidentSummaryEndpoint,
  getTenantIncidentEndpoint,
  getTenantIncidentSummaryEndpoint,
  listFleetIncidentsEndpoint,
  listTenantIncidentsEndpoint,
  type IncidentDetail,
  type IncidentSummary,
  type IncidentSummaryPeriod,
  type IncidentSummaryResponse,
} from "../../client-messages/incidents";
import { useCursorPager, useSkipEmptyPages } from "./cursor-pager";
import type { UseActivationsResult, UseFleetListResult } from "./hooks";
import { incidentFiltersKey, type FleetIncidentFilters, type IncidentFilters } from "./incident-filters";

const listTenantIncidentsQuery = queryFor(listTenantIncidentsEndpoint);
const listFleetIncidentsQuery = queryFor(listFleetIncidentsEndpoint);
const getTenantIncidentQuery = queryFor(getTenantIncidentEndpoint);
const getFleetIncidentQuery = queryFor(getFleetIncidentEndpoint);
const getTenantIncidentSummaryQuery = queryFor(getTenantIncidentSummaryEndpoint);
const getFleetIncidentSummaryQuery = queryFor(getFleetIncidentSummaryEndpoint);

/** Rows per page of an incident list. The endpoint caps at 100. */
export const INCIDENTS_PAGE_SIZE = 25;

/** A page of incidents, held and paged exactly as a page of activations is. */
export type UseIncidentsResult = UseActivationsResult<IncidentSummary>;

export interface UseIncidentsOptions {
  readonly filters: IncidentFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/** One page of a tenant's genuine incidents, newest first, narrowed by site and camera. */
export function useTenantIncidents({ filters, enabled = true }: UseIncidentsOptions): UseIncidentsResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(incidentFiltersKey(filters));
  const query = useQuery({
    ...listTenantIncidentsQuery(
      apiConfig,
      {},
      { limit: INCIDENTS_PAGE_SIZE, ...filters, ...(pager.cursor !== undefined && { cursor: pager.cursor }) },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });
  return pageResult(query, pager);
}

export interface UseFleetIncidentsOptions {
  readonly filters: FleetIncidentFilters;
  /** Gate the query — e.g. while the filters in the URL are invalid. */
  readonly enabled?: boolean;
}

/**
 * One page of every genuine incident, newest first, or those of the clients a
 * client number starts. Paged like `useFleetActivations`, skipping the empty
 * pages a quiet stretch of months answers with the same way.
 */
export function useFleetIncidents({
  filters,
  enabled = true,
}: UseFleetIncidentsOptions): UseFleetListResult<IncidentSummary> {
  const apiConfig = useApiConfig();
  const pager = useCursorPager(incidentFiltersKey(filters));
  const query = useQuery({
    ...listFleetIncidentsQuery(
      apiConfig,
      {},
      { limit: INCIDENTS_PAGE_SIZE, ...filters, ...(pager.cursor !== undefined && { cursor: pager.cursor }) },
    ),
    enabled,
    placeholderData: keepPreviousData,
  });
  const skipping = useSkipEmptyPages(pager, query.data, query.isPlaceholderData);
  const page = pageResult(query, pager);

  return {
    ...page,
    items: skipping ? undefined : page.items,
    clientNos: query.isPlaceholderData ? undefined : query.data?.clientNos,
    isLoading: page.isLoading || skipping,
  };
}

const pageResult = (
  query: {
    data?: { items: IncidentSummary[]; cursor: string | null } | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isPlaceholderData: boolean;
    error: Error | null;
    refetch: () => unknown;
  },
  pager: ReturnType<typeof useCursorPager>,
): UseIncidentsResult => ({
  items: query.data?.items,
  isLoading: query.isLoading,
  isFetching: query.isFetching,
  isPlaceholderData: query.isPlaceholderData,
  error: query.error,
  refetch: () => void query.refetch(),
  pager,
  // A placeholder's cursor belongs to the page being left — see `useTenantActivations`.
  nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
});

export interface UseIncidentResult {
  readonly incident: IncidentDetail | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/** One incident from the tenant route, which serves it only when it is counted under the tenant. */
export function useTenantIncident(incidentId: string): UseIncidentResult {
  const apiConfig = useApiConfig();
  const query = useQuery({ ...getTenantIncidentQuery(apiConfig, { incidentId }), enabled: incidentId !== "" });
  return { incident: query.data, isLoading: query.isLoading, error: query.error, refetch: () => void query.refetch() };
}

/** One incident from the fleet route, whichever tenant it is counted under, or none. */
export function useFleetIncident(incidentId: string): UseIncidentResult {
  const apiConfig = useApiConfig();
  const query = useQuery({ ...getFleetIncidentQuery(apiConfig, { incidentId }), enabled: incidentId !== "" });
  return { incident: query.data, isLoading: query.isLoading, error: query.error, refetch: () => void query.refetch() };
}

export interface UseIncidentSummaryOptions {
  readonly filters: IncidentFilters;
  readonly period: IncidentSummaryPeriod;
  /** The IANA zone whose days, weeks or months the counts are summed into. */
  readonly timeZone: string;
  readonly enabled?: boolean;
}

export interface UseIncidentSummaryResult {
  readonly summary: IncidentSummaryResponse | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/** A tenant's counts over the filtered window, or a site's or camera's. */
export function useTenantIncidentSummary({
  filters,
  period,
  timeZone,
  enabled = true,
}: UseIncidentSummaryOptions): UseIncidentSummaryResult {
  const apiConfig = useApiConfig();
  const { since, until, site, camera } = filters;
  const query = useQuery({
    ...getTenantIncidentSummaryQuery(
      apiConfig,
      {},
      {
        since,
        timeZone,
        period,
        ...(until !== undefined && { until }),
        ...(site !== undefined && { site }),
        ...(camera !== undefined && { camera }),
      },
    ),
    // No placeholder: each row's period label belongs to the query that
    // fetched it, and the page links rows by the period now chosen.
    enabled,
  });
  return summaryResult(query);
}

/** The whole fleet's counts over the filtered window. */
export function useFleetIncidentSummary({
  filters,
  period,
  timeZone,
  enabled = true,
}: UseIncidentSummaryOptions): UseIncidentSummaryResult {
  const apiConfig = useApiConfig();
  const { since, until } = filters;
  const query = useQuery({
    ...getFleetIncidentSummaryQuery(apiConfig, {}, { since, timeZone, period, ...(until !== undefined && { until }) }),
    // No placeholder, as for the tenant summary: earlier rows must not show under a new period.
    enabled,
  });
  return summaryResult(query);
}

const summaryResult = (query: {
  data?: IncidentSummaryResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => unknown;
}): UseIncidentSummaryResult => ({
  summary: query.data,
  isLoading: query.isLoading,
  isFetching: query.isFetching,
  error: query.error,
  refetch: () => void query.refetch(),
});
